import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { autoScrollViewHelper } from '../../../global/modules/autoScrollVewHelper';
import { useGuidedRegContext } from '../../context/guidedRegContext';
import { SAVE_FORM_DATA } from '../../context/reducer';
import { validatePostalCodeChange, validateZipCodeChange } from '../../../cca/util/ccaUtil';
import { getAVSValidationAPI } from '../../../cca/components/addrAutocomplete/utils/addressUtils';
import {
    COUNTRY,
    COUNTRY_LIST,
    ADDRESS,
    ADDRESS2,
    CITY,
    STATE,
    ZIP_CODE,
    IS_AGREE,
    IS_PRIVACY_POLICY,
    DRIVERS_LICENSE_STATE,
    DRIVERS_LICENSE_NUMBER,
    STEP_NAMES,
    ONE,
    SEVEN,
    FIVE,
    ALPHANUMERIC_REGEX,
    TWO,
    IS_SMS_OPTIN,
    PHONE_NUMBER_VERIFICATION,
    IS_ELECTRONIC_MESSAGE
} from '../constants';
import { logGRError, refreshTokenCall } from '../../utils/guidedUtil';
import { createAccount } from '../../api/apiRequest';
import { createAccountPayload } from '../utils/createAccountPayload';
import {
    DL_EXIST_ERROR_CODE,
    JOIN_ACCOUNT_ERROR_CODE,
    JOIN_ACCOUNT_ERROR_SCREEN,
    PHONE_NUMBER_VERIFICATION_CODE,
    THANK_YOU_PATH
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import isObjectEmpty from '../../../../aem-core-components/utils/isObjectEmpty';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../constants/analyticsConstants/Ecommerce';
import { STORAGE_CONFIG } from '../../../../constants/storageConfig';
import { useAnalyticsContext } from '../../../../config/GoogleTagManagerEvents';
import { getAEMCashFlowAlterableData, navigateToPath } from '../utils/cashflowUtils';
import { prefetchResources } from '../../../global/utils/prefetch';
/**
 * This function creates and manages the form state for the account information.
 *
 * @return {Object} An object containing the account state and various functions to handle form changes and validations.
 */
const useAccountFormState = () => {
    const [{ formData }, guidedRegDispatch] = useGuidedRegContext();
    const [autoScrolling, setAutoScrolling] = useState(false);
    const [verifyAddressModal, setVerifyAddressModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dlExist, setDlExist] = useState(false);
    const [formStarted, seFormStarted] = useState('');
    const navigate = useNavigate();
    const intl = useIntl();
    const {
        country,
        address,
        address2,
        city,
        state,
        zipCode,
        isAgree,
        isSmsOptin,
        isPrivacyPolicy,
        driversLicenseState,
        driversLicenseNumber
    } = formData[STEP_NAMES[1]]?.data || '';
    const { sendEventsForCreateCashForm, sendEventsForCreateCashFormError, sendEventsForCreateCashFormStepViewed } =
        useAnalyticsContext();
    const { commonConfig = {}, accountScreenConfig = {} } = getAEMCashFlowAlterableData() || {};
    const { createAccountButtonLabel = '' } = commonConfig || {};
    const { accountInfoTitle = '' } = accountScreenConfig || {};

    const translationValues = {
        country: intl.formatMessage({ id: 'select_country' }),
        address: intl.formatMessage({ id: 'enter_address' }),
        city: intl.formatMessage({ id: 'enter_city' }),
        state: intl.formatMessage({ id: 'select_state' }),
        province: intl.formatMessage({ id: 'select_province' }),
        zipCode: intl.formatMessage({ id: 'enter_zip_code' }),
        postalCode: intl.formatMessage({ id: 'enter_postal_code' }),
        isPrivacyPolicy: intl.formatMessage({ id: 'is_privacy_policy' }),
        driversLicenseState: intl.formatMessage({ id: 'select_drivers_license_state' }),
        driversLicenseNumber: intl.formatMessage({ id: 'enter_drivers_license_number' }),
        validZipcode: intl.formatMessage({ id: 'enter_valid_zipcode' }),
        validPostalCode: intl.formatMessage({ id: 'enter_valid_postal_code' }),
        validAddress: intl.formatMessage({ id: 'enter_valid_address' }),
        driverLicenseExists: intl.formatMessage({ id: 'driver_license_exists' }),
        contactUs: intl.formatMessage({ id: 'contact_us' }),
        privacyPolicy: intl.formatMessage({ id: 'privacy_policy' }),
        consentError: intl.formatMessage({ id: 'consent_error' }),
    };

    const [accountState, setAccountState] = useState({
        data: {
            [COUNTRY]: country,
            [ADDRESS]: address,
            [ADDRESS2]: address2,
            [CITY]: city,
            [STATE]: state,
            [ZIP_CODE]: zipCode,
            [IS_AGREE]: isAgree,
            [IS_SMS_OPTIN]: isSmsOptin,
            [DRIVERS_LICENSE_STATE]: driversLicenseState,
            [DRIVERS_LICENSE_NUMBER]: driversLicenseNumber,
            [IS_PRIVACY_POLICY]: isPrivacyPolicy,
            [IS_ELECTRONIC_MESSAGE]: '',
        },
        error: {
            [COUNTRY]: '',
            [ADDRESS]: '',
            [CITY]: '',
            [STATE]: '',
            [ZIP_CODE]: '',
            [DRIVERS_LICENSE_STATE]: '',
            [DRIVERS_LICENSE_NUMBER]: '',
            [IS_PRIVACY_POLICY]: '',
            [IS_ELECTRONIC_MESSAGE]: '',
        }
    });

    const isCountryCanada = accountState.data[COUNTRY] === COUNTRY_LIST[1].value;
    const validationRules = {
        [COUNTRY]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.country
            }
        ],
        [ADDRESS]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.address
            }
        ],
        [CITY]: [
            {
                condition: value => value.trim() === '',
                message: translationValues.city
            }
        ],
        [STATE]: [
            {
                condition: value => value.trim() === '',
                message: isCountryCanada ? translationValues.province : translationValues.state
            }
        ],
        [ZIP_CODE]: [
            {
                condition: value => value.trim() === '',
                message: isCountryCanada ? translationValues.postalCode : translationValues.zipCode
            },
            {
                condition: value => value.length < SEVEN && isCountryCanada,
                message: translationValues.validPostalCode
            },
            {
                condition: value => value.length < FIVE && !isCountryCanada,
                message: translationValues.validZipcode
            }
        ],
        [IS_PRIVACY_POLICY]: [
            {
                condition: value => !value && !isCountryCanada,
                message: translationValues.isPrivacyPolicy
            }
        ],
        [DRIVERS_LICENSE_STATE]: [
            {
                condition: value => value.trim() === '' && !isCountryCanada,
                message: translationValues.driversLicenseState
            }
        ],
        [DRIVERS_LICENSE_NUMBER]: [
            {
                condition: value => value.trim() === '' && !isCountryCanada,
                message: translationValues.driversLicenseNumber
            }
        ],
        [IS_ELECTRONIC_MESSAGE]: [
            {
                condition: value => value.trim() === '' && isCountryCanada,
                message: translationValues.consentError
            }
        ],
    };

    const handleAutoScroll = () => {
        const filteredError = Object.fromEntries(
            Object.entries(accountState?.error || {}).filter(([key, value]) => value !== '')
        );
        autoScrollViewHelper(filteredError);
    };

    useEffect(() => {
        handleAutoScroll();
    }, [autoScrolling]);

    /**
     * Updates the account state with the provided form data.
     *
     * @param {string} name - the name of the form field
     * @param {any} value - the value of the form field
     * @return {void}
     */
    const saveFormDataState = (name, value) => {
        setAccountState(prevState => {
            if (
                name === IS_ELECTRONIC_MESSAGE
                && value === 'No'
            ) {
                // If user selects 'No' for electronic message, clear isSmsOptIn and isSpecialOffers
                return {
                    ...prevState,
                    data: {
                        ...prevState.data,
                        [IS_SMS_OPTIN]: false,
                        [IS_AGREE]: false,
                        [name]: value
                    },
                    error: {
                        ...prevState.error,
                        [name]: ''
                    }
                };
            }
            return {
                ...prevState,
                data: {
                    ...prevState.data,
                    [name]: value
                },
                error: {
                    ...prevState.error,
                    [name]: ''
                }
            };
        });
    };

    /**
     * Saves the error state of a form field.
     *
     * @param {string} name - the name of the form field
     * @param {string} error - the error message
     * @return {void}
     */
    const saveFormErrorState = (name, error) => {
        setAccountState(prevState => ({
            ...prevState,
            error: {
                ...prevState.error,
                [name]: error
            }
        }));
    };

    /**
     * Checks if the form is valid based on the provided validation rules.
     *
     * @return {boolean} true if the form has error, false if the form is valid
     */
    const isFormValid = () => {
        let isFormValid = true;
        for (const field in validationRules) {
            if (validationRules.hasOwnProperty(field)) {
                const rules = validationRules[field];
                for (const rule of rules) {
                    if (rule.condition(accountState.data[field])) {
                        saveFormErrorState(field, rule.message);
                        isFormValid = false;
                        sendAnalyticsForFieldError(field, rule.message);
                        break;
                    }
                }
            }
        }
        return isFormValid;
    };

    const sendAnalyticsOnFinalSubmit = () => {
        sendEventsForCreateCashFormStepViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_STEP_COMPLETED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            accountInfoTitle,
            window.location.href,
            TWO
        );
    };

    const sendAnalyticsForFieldError = (field, errorMessage) => {
        sendEventsForCreateCashFormError(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_ERROR,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            field,
            errorMessage,
            TWO
        );
    };

    /**
     * Handles the change event of the drop down.
     *
     * @param {type} name - description of parameter
     * @param {type} value - description of parameter
     * @return {type} description of return value
     */
    const handleDropDownChange = (name, value) => {
        saveFormDataState(name, value);
        if (name === COUNTRY) {
            saveFormDataState(ADDRESS, '');
            saveFormDataState(ADDRESS2, '');
            saveFormDataState(CITY, '');
            saveFormDataState(STATE, '');
            saveFormDataState(ZIP_CODE, '');
            saveFormDataState(DRIVERS_LICENSE_STATE, '');
            saveFormDataState(DRIVERS_LICENSE_NUMBER, '');
        }
    };

    const handleZipCodeChange = e => {
        const { name, value } = e.target;
        accountState.data[COUNTRY] === COUNTRY_LIST[1].value
            ? saveFormDataState(name, validatePostalCodeChange(value))
            : saveFormDataState(name, validateZipCodeChange(value));
    };
    /**
     * Handles the input change event.
     *
     * @param {Event} e - The input change event
     * @return {void}
     */
    const handleInputChange = e => {
        const { name = '', value = '' } = e?.target || {};
        if (name == DRIVERS_LICENSE_NUMBER) {
            const sanitizeValue = value.toUpperCase().replace(ALPHANUMERIC_REGEX, '');
            saveFormDataState(name, sanitizeValue);
        } else {
            saveFormDataState(name, value);
        }
    };

    /**
     * Handles the change in cash location and updates the form data state accordingly.
     *
     * @param {object} addressObj - the address object containing address, city, state, and zip code
     * @return {void}
     */
    const handleCashLocationChange = addressObj => {
        saveFormDataState(ADDRESS2, '');
        const { address = '', city = '', state = '', zipCode = '' } = addressObj || {};
        saveFormDataState(ADDRESS, address);
        saveFormDataState(CITY, city);
        saveFormDataState(STATE, state);
        saveFormDataState(ZIP_CODE, zipCode);
    };

    /**
     * Sets the cash address manually.
     *
     * @param {type} value - the value to set as the cash address
     * @return {type} undefined
     */
    const setCashAddressManually = value => {
        saveFormDataState(ADDRESS, value);
    };

    /**
     * Handle the change event of a checkbox.
     *
     * @param {string} label - The label of the checkbox.
     * @param {boolean} isChecked - The current checked state of the checkbox.
     * @param {number} id - The unique identifier of the checkbox.
     * @return {void}
     */
    const handleCheckboxChange = (label, isChecked, id) => {
        saveFormDataState(id, isChecked);
        handleFormStartedForAnalytic(id);
    };

    /**
     * Handle focus event for the specified field.
     *
     * @param {type} field - description of the field parameter
     * @return {type} description of the return value
     */
    const handleFocus = field => {
        saveFormErrorState(field, '');
        handleFieldInteraction(field);
    };

    /**
     * Handle the submit event.
     *
     * @param {type} e - the event object
     * @return {type} description of return value
     */
    const handleSubmit = e => {
        e.preventDefault();
        // your submit logic here
    };

    const saveDataInContext = async () => {
        const payload = {
            step: STEP_NAMES[ONE],
            data: {
                ...formData[STEP_NAMES[ONE]]?.data,
                ...accountState.data
            }
        };
        await guidedRegDispatch({ type: SAVE_FORM_DATA, payload });
    };

    /**
     * A function that handles the action when the continue button is clicked.
     */

    const onContinue = async () => {
        setDlExist(false);
        sendSubmittedAnalyticsEvent();
        if (!(accountState.error?.[ADDRESS] === translationValues.validAddress)) {
            setAutoScrolling(!autoScrolling);
        } else {
            handleFocus(ADDRESS);
        }
        if (isFormValid()) {
            setIsLoading(true);
            prefetchResources(THANK_YOU_PATH);
            if (await avsValidationCheck()) {
                // saving the data in context
                await saveDataInContext();
                // call api here for submission
                await createCashAccount();
            } else {
                handleAutoScroll();
                setIsLoading(false);
            }
        }
    };

    const createCashAccount = async () => {
        try {
            await refreshTokenCall();
            const country = isCountryCanada ? TWO : ONE;

            const {
                data: { data: response = {} } = {},
                error: { response: { data: { error: { errorCode: errorCode = '' } = {} } = {} } = {} } = {}
            } =
                (await createAccount(
                    country,
                    createAccountPayload({
                        ...formData,
                        ...{
                            [STEP_NAMES[ONE]]: {
                                ...accountState
                            }
                        }
                    })
                )) || {};

            if (errorCode == DL_EXIST_ERROR_CODE) {
                //set error code to DL
                setDlExist(true);
            } else if (errorCode == JOIN_ACCOUNT_ERROR_CODE) {
                //navigate to error screen
                window.onbeforeunload = null;
                window.location.href = JOIN_ACCOUNT_ERROR_SCREEN;
            } else if (errorCode == PHONE_NUMBER_VERIFICATION_CODE) {
                //navigate to phone number verification screen
                navigateToPath(navigate, PHONE_NUMBER_VERIFICATION);
            } else if (!isObjectEmpty(response)) {
                sendAnalyticsOnFinalSubmit();
                // navigate to thank-you page
                window.onbeforeunload = null;
                const url = window?.location?.protocol + '//' + window?.location?.host + THANK_YOU_PATH;
                window.location.href = url;
            } else {
                throw errorCode;
            }
        } catch (error) {
            window.onbeforeunload = null;
            logGRError(error);
        }
        setIsLoading(false);
    };

    const avsValidationCheck = async () => {
        const addressObj = {
            address: accountState?.data?.[ADDRESS],
            addressLine2: accountState?.data?.[ADDRESS2],
            city: accountState?.data?.[CITY],
            state: accountState?.data?.[STATE],
            zipCode: accountState?.data?.[ZIP_CODE]
        };
        const avsResp = await getAVSValidationAPI(addressObj, isCountryCanada ? 2 : 1, 'delivery');
        const { data: { data: { addressInfo: { addrResult = '' } = {} } = {} } = {} } = avsResp || {};
        const isValidAddress = addrResult === '0' || addrResult === '1';
        if (!isValidAddress) {
            setVerifyAddressModal(!verifyAddressModal);
            return false;
        }
        return true;
    };

    const handleFieldInteraction = fieldName => {
        !formStarted && seFormStarted(fieldName);
        sendEventsForCreateCashForm(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_INTERACTED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            fieldName,
            TWO
        );
    };

    useEffect(() => {
        formStarted &&
            sendEventsForCreateCashForm(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_STARTED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
                formStarted,
                TWO
            );
    }, [formStarted]);

    const sendSubmittedAnalyticsEvent = () => {
        sendEventsForCreateCashForm(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FIELD_FORM_SUBMITTED_EVENT,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            createAccountButtonLabel,
            TWO,
            sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID)
        );
    };

    const handleFormStartedForAnalytic = fieldName => {
        !formStarted && seFormStarted(fieldName);
        sendEventsForCreateCashForm(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_INTERACTED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
            fieldName,
            TWO
        );
    };

    useEffect(() => {
        accountInfoTitle &&
            sendEventsForCreateCashFormStepViewed(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_FORM_VIEW_EVENT,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CREATE_CASH_ACCOUNT,
                accountInfoTitle,
                window.location.href,
                TWO
            );
    }, [accountInfoTitle]);

    return {
        accountState,
        translationValues,
        handleDropDownChange,
        handleInputChange,
        handleCashLocationChange,
        setCashAddressManually,
        handleCheckboxChange,
        handleFocus,
        handleSubmit,
        handleZipCodeChange,
        handleFieldInteraction,
        onContinue,
        isCountryCanada,
        verifyAddressModal,
        setVerifyAddressModal,
        setAutoScrolling,
        autoScrolling,
        saveFormErrorState,
        isLoading,
        dlExist,
        createCashAccount
    };
};

export default useAccountFormState;
